import { FlexColumn, FlexRow, MaterialIcon, StyledButton } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { DatePicker } from 'baseui/datepicker';
import Filter from 'baseui/table/filter';
import { memo, useEffect, useMemo } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useStyles } from '../../hooks/useStyles';
import { jobsFiltersState, searchJobsState } from '../../storage/jobStates';
import { JobFilters } from '../../types/jobs';
import { TenantSelect } from '../tenants/TenantSelect';
import StyledTooltip from '../ui/StyledTooltip';
import { UserSelect } from '../users/UserSelect';
import { JobStatusMultiSelect } from './JobStatusMultiSelect';
import { JobTypeSelect } from './types/JobTypeSelect';

type Props = {
  disabledStatusFilter?: boolean;
  disabledDateFilter?: boolean;
};

function JobsFilters({ disabledStatusFilter = false, disabledDateFilter = false }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const [filters, setFilters] = useRecoilState(jobsFiltersState);
  const resetSearch = useResetRecoilState(searchJobsState);
  const resetFilters = useResetRecoilState(jobsFiltersState);

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, []);

  const onFiltersChange = (key: keyof JobFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isActive = useMemo(() => {
    return Object.keys(filters).some((key) => !!filters[key as keyof JobFilters]);
  }, [filters]);

  const onResetFilters = () => {
    resetSearch();
    resetFilters();
  };

  return (
    <FlexRow
      gap={0}
      classNames={css({ alignItems: 'center' })}
      centered
    >
      <Filter
        active={isActive}
        overrides={{
          Heading: {
            component: () => <></>,
          },
          Footer: {
            component: () => {
              return (
                <FlexRow
                  classNames={css({
                    justifyContent: 'end',
                    padding: theme.sizing.scale200,
                  })}
                >
                  <StyledButton
                    onClick={onResetFilters}
                    size="mini"
                    kind="tertiary"
                  >
                    Restablecer filtros
                  </StyledButton>
                </FlexRow>
              );
            },
          },
          Content: {
            style: () => ({
              borderTop: 'none',
              maxHeight: '250px',
            }),
          },
        }}
      >
        <FlexColumn
          classNames={css({
            paddingTop: theme.sizing.scale500,
            paddingBottom: theme.sizing.scale500,
          })}
          gap={theme.sizing.scale200}
        >
          <UserSelect
            selectedId={filters.user?._id}
            onChange={(data) => onFiltersChange('user', data)}
            showEmptyOption
            inline
            isFilter
            smallContainer
            dropdownOffset="0"
          />
          <TenantSelect
            selectedTenantId={filters.tenant?._id}
            onChangeTenant={(data) => onFiltersChange('tenant', data ? { _id: data._id, name: data.name } : undefined)}
            showEmptyOption
            inline
            isFilter
            smallContainer
            dropdownOffset="90px"
          />
          <JobTypeSelect
            selectedId={filters.jobType?._id}
            onChange={(data) => onFiltersChange('jobType', data)}
            showEmptyOption
            inline
            isFilter
            smallContainer
            dropdownOffset="132px"
          />
          {!disabledStatusFilter && (
            <JobStatusMultiSelect
              selectedJobStatusIds={filters.jobStatus}
              onChangeJobStatusIds={(ids) => onFiltersChange('jobStatus', ids)}
              inline
              isFilter
              smallContainer
              dropdownOffset="175px"
            />
          )}
          {!disabledDateFilter && (
            <DatePicker
              placeholder="Fecha inicio - Fecha fin"
              range
              size="compact"
              value={[
                filters.startDate ? new Date(filters.startDate) : undefined,
                filters.endDate ? new Date(filters.endDate) : undefined,
              ]}
              onChange={({ date: dates }) => {
                if (dates instanceof Array) {
                  onFiltersChange('startDate', dates[0]?.getTime());
                  const endDate: Date | undefined = dates[1] || undefined;
                  endDate?.setHours(23, 59);
                  onFiltersChange('endDate', endDate?.getTime());
                }
              }}
            />
          )}
        </FlexColumn>
      </Filter>
      <StyledTooltip
        content={'Restablecer filtros'}
        showArrow={false}
      >
        <Button
          size="compact"
          shape="square"
          onClick={onResetFilters}
          kind="tertiary"
        >
          <MaterialIcon
            name={'recycling'}
            size="compact"
          />
        </Button>
      </StyledTooltip>
    </FlexRow>
  );
}

export default memo(JobsFilters);
